/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnChanges, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/service/auth.service';
import { NewsletterApiService } from 'src/app/shared/services/customer-api/newsletter-api.service';
import { NewsletterContentFormatService } from '../services/newsletter-content-format.service';
import { NewsletterService } from 'src/app/service/newsletter.service';
import { RoutingService } from 'src/app/service/routing.service';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { range, Subject } from 'rxjs';

// Backend is serial within a request but parallel across requests
// (~3s/section, 60s timeout) — fan out rather than batch.
const PREVIEW_FETCH_CONCURRENCY = 4;

@Component({
  selector: 'app-newsletter-preview',
  templateUrl: './newsletter-preview.component.html',
  styleUrls: ['./newsletter-preview.component.scss'],
})
export class NewsletterPreviewComponent implements OnChanges, OnDestroy {
  newsletter: any;
  newsletterPreview: string;
  loading: boolean;
  errorMessage: boolean;

  params: any;
  previousValue: any;

  selectedArticles = [];

  scrollIndex: number;
  readyscroll: boolean;

  numSections: number;

  newsletterContent: any[][] = [];

  private loadedSections = new Set<number>();
  // Per-load cancellation — fires on every new load so stale responses don't leak into fresh state.
  private cancelInflight$ = new Subject<void>();
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private onDocumentClick = (e: MouseEvent) => this.handleDismissClick(e);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public auth: AuthService,
    private newsletterApi: NewsletterApiService,
    private contentFormatService: NewsletterContentFormatService,
    public newsletterService: NewsletterService,
    public routing: RoutingService,
  ) {
    document.body.addEventListener('click', this.onDocumentClick);

    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        this.params = JSON.parse(JSON.stringify(params));
        if (this.routing.reFresh(params, this.previousValue, ['id', 'rdn'])) {
          if (params.id && params.id !== 'create') {
            this.resetPreviewState();
            this.loadAllSections();
          } else if (params.id === 'create') {
            this.resetPreviewState();
            this.readyscroll = true;
            this.loading = false;
          }
        }
        this.previousValue = params;
      });
  }

  ngOnChanges() {
    this.newsletter = this.newsletterService.newsletter;
  }

  ngOnDestroy(): void {
    document.body.removeEventListener('click', this.onDocumentClick);
    this.cancelInflight$.next();
    this.cancelInflight$.complete();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // Injected checkboxes live inside a sanitized innerHTML blob with no
  // Angular bindings, so we delegate click handling at document level.
  private handleDismissClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.matches?.('input[data-dismiss-news]')) {
      return;
    }
    try {
      this.dismissNews(JSON.parse((target as HTMLInputElement).value));
    } catch {
      /* malformed value — skip */
    }
  }

  dismissNews(item) {
    let check = false;
    const filteredArticles = this.selectedArticles.filter((news) => {
      if (news.news === item.news) {
        check = true;
      }
      return news.news !== item.news;
    });
    if (check) {
      this.selectedArticles = filteredArticles;
    } else {
      this.selectedArticles.push(item);
    }
  }

  dismissManyNews() {
    if (this.selectedArticles.length !== 0) {
      const newsletterId =
        this.routing.isNewsletter() &&
        this.previousValue &&
        this.previousValue.id
          ? this.previousValue.id
          : null;
      if (
        window.confirm(
          'Do you want to delete all selected articles from the newsletter?',
        )
      ) {
        const newsIds = this.selectedArticles.map((el) => el.news);
        const uniqueSections = [
          ...new Set(this.selectedArticles.map((item) => item.section)),
        ];
        const resourceType = this.contentFormatService.getCurrentResourceType();
        this.newsletterApi
          .dismissNews(newsIds, newsletterId, resourceType)
          .subscribe(() => {
            this.selectedArticles = [];
            uniqueSections.forEach((section) => {
              this.reloadSection(Number(section));
            });
          });
      }
    }
  }

  private resetPreviewState() {
    this.cancelInflight$.next();
    this.loading = true;
    this.errorMessage = false;
    this.selectedArticles = [];
    this.scrollIndex = 0;
    this.readyscroll = false;
    this.numSections = null;
    this.newsletterContent = [];
    this.newsletterPreview = '';
    this.loadedSections = new Set<number>();
  }

  private loadAllSections() {
    this.newsletterApi
      .previewNewsletter(this.params.id, 0, 1)
      .pipe(takeUntil(this.cancelInflight$), takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          this.numSections = res.num_sections;
          this.newsletterContent = Array.from(
            { length: this.numSections || 0 },
            () => [],
          );
          this.onSectionLoaded(0, res);

          if (this.numSections > 1) {
            range(1, this.numSections - 1)
              .pipe(
                mergeMap(
                  (index) =>
                    this.newsletterApi
                      .previewNewsletter(this.params.id, index, index + 1)
                      .pipe(map((response) => ({ index, response }))),
                  PREVIEW_FETCH_CONCURRENCY,
                ),
                takeUntil(this.cancelInflight$),
                takeUntil(this.ngUnsubscribe),
              )
              .subscribe({
                next: ({ index, response }) =>
                  this.onSectionLoaded(index, response),
                error: () => this.markPreviewError(),
              });
          }
        },
        error: () => this.markPreviewError(),
      });
  }

  private reloadSection(sectionIndex: number) {
    this.newsletterApi
      .previewNewsletter(this.params.id, sectionIndex, sectionIndex + 1)
      .pipe(takeUntil(this.cancelInflight$), takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => this.renderPreview(sectionIndex, res),
        error: () => this.markPreviewError(),
      });
  }

  private markPreviewError() {
    this.readyscroll = true;
    this.loading = false;
    this.errorMessage = true;
  }

  private onSectionLoaded(sectionIndex: number, res: any) {
    this.loadedSections.add(sectionIndex);
    this.scrollIndex = this.loadedSections.size;
    this.renderPreview(sectionIndex, res);
    if (sectionIndex === 0) {
      this.loading = false;
    }
  }

  private renderPreview(sectionIndex: number, res: any) {
    const doc = new DOMParser().parseFromString(res.html, 'text/html');
    const newsletterBody = doc.getElementsByClassName('body');
    if (!newsletterBody[0]) {
      return;
    }

    const outerElementsBeforeSection: Element[] = [];
    const outerElementsAfterSection: Element[] = [];
    const sectionsInOrder: Element[] = [];
    let isBeforeSection = true;

    Array.from(newsletterBody[0].children).forEach((el) => {
      if (el.className.includes('newsletter-section')) {
        isBeforeSection = false;
        sectionsInOrder.push(el);
      } else if (el.innerHTML.includes('No new information')) {
        isBeforeSection = false;
      } else if (isBeforeSection) {
        outerElementsBeforeSection.push(el);
      } else {
        outerElementsAfterSection.push(el);
      }
    });

    sectionsInOrder.forEach((sectionEl, localIdx) => {
      const absoluteIndex = sectionIndex + localIdx;
      this.enrichSectionNewsRows(sectionEl, absoluteIndex);
      this.newsletterContent[absoluteIndex] = [sectionEl];
    });

    const contiguousLimit = this.contiguousPrefixLimit();
    let bodyInnerHTML = '';
    outerElementsBeforeSection.forEach((el) => {
      bodyInnerHTML += el.innerHTML;
    });
    this.newsletterContent.slice(0, contiguousLimit).forEach((section) => {
      section.forEach((el) => {
        bodyInnerHTML += el.innerHTML;
      });
    });
    outerElementsAfterSection.forEach((el) => {
      bodyInnerHTML += el.innerHTML;
    });
    newsletterBody[0].innerHTML = bodyInnerHTML;

    // Re-apply `checked` on articles the user had selected. Filtering by our
    // own marker keeps us safe if the template ever adds unrelated inputs.
    const selectedIds = new Set(this.selectedArticles.map((a) => a.news));
    doc
      .querySelectorAll<HTMLInputElement>('input[data-dismiss-news]')
      .forEach((checkbox) => {
        try {
          const { news } = JSON.parse(checkbox.value);
          if (selectedIds.has(news)) {
            checkbox.setAttribute('checked', 'checked');
          }
        } catch {
          /* malformed value — skip */
        }
      });

    this.newsletterPreview = doc.documentElement.innerHTML;
    this.readyscroll = true;
  }

  // Index of the first not-yet-loaded section. Rendering only up to this
  // point avoids visible gaps when later batches arrive before earlier ones.
  private contiguousPrefixLimit(): number {
    for (let i = 0; i < (this.numSections || 0); i++) {
      if (!this.loadedSections.has(i)) {
        return i;
      }
    }
    return this.numSections || 0;
  }

  // Prepend a dismissal checkbox to each `.news-row`, reading the article id
  // from the row's own `<a class="news-link">` href. Rows with no extractable
  // id are skipped rather than producing a "dismiss nothing" checkbox.
  private enrichSectionNewsRows(sectionEl: Element, sectionIndex: number) {
    const newsRows = Array.from(sectionEl.getElementsByClassName('news-row'));
    newsRows.forEach((row) => {
      const newsId = this.extractNewsId(row);
      if (!newsId) {
        return;
      }
      (row as HTMLElement).style.position = 'relative';
      row.innerHTML =
        `<input type="checkbox" data-dismiss-news value='{"news":"${newsId}","section":"${sectionIndex}"}' ` +
        `style='position: absolute; left: -35px; accent-color: #1E816C;'>` +
        row.innerHTML;
    });
  }

  private extractNewsId(newsRow: Element): string {
    const link = newsRow.getElementsByClassName('news-link')[0] as
      | HTMLAnchorElement
      | undefined;
    if (!link?.href) {
      return '';
    }
    try {
      return new URL(link.href).searchParams.get('news') ?? '';
    } catch {
      return '';
    }
  }
}
